// ─────────────────────────────────────────────────────────────────────────────
// GuidelinesDashboard.jsx — Before/After 인라인 비교 + 레벨 필터 + 오늘의 원칙 위젯
// ─────────────────────────────────────────────────────────────────────────────

import { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Network, FastForward, GitMerge, Lightbulb,
  Layers, FileText, Type, Target,
  BarChart3, AlignLeft, BookOpen, Zap,
  Users, SplitSquareHorizontal, Key, AlertTriangle,
  BarChart2, XCircle, Clock, Siren, Mic, X,
  ChevronDown, ChevronRight, ArrowRight,
  CheckCircle2, XCircle as XIcon,
  Star, Flame, Award, CheckSquare, Square, RefreshCw,
  Smartphone, LayoutGrid, Layers3, Briefcase, Activity, Search
} from 'lucide-react';
import { AUGMENTED_PRINCIPLES, CATEGORIES } from '../data/notebooklmData';
import { useGuidelineFavorites } from '../hooks/useGuidelineFavorites';

// ── 뷰 모드 탭 설정 ─────────────────────────────────────────────────────────────
const VIEW_MODES = [
  { id: 'phase', label: '단계별', icon: Layers3 },
  { id: 'category', label: '영역별', icon: LayoutGrid },
  { id: 'target', label: '독자(타겟)별', icon: Users },
  { id: 'situation', label: '상황별', icon: Activity },
  { id: 'checklist', label: '자가진단', icon: CheckSquare },
  { id: 'fav', label: '즐겨찾기', icon: Star },
];

// ── 아이콘 맵 ────────────────────────────────────────────────────────────────
const ICON_MAP = {
  Network, FastForward, GitMerge, Lightbulb,
  Layers, FileText, Type, Target,
  BarChart3, AlignLeft, BookOpen, Zap,
  Users, SplitSquareHorizontal, Key, AlertTriangle,
  BarChart2, XCircle, Clock, Siren, Mic,
};

// ── 단계별(Phase) 설정 ───────────────────────────────────────────────────────
const PHASE_CONFIG = {
  기획: {
    label: '기획',
    emoji: '🎯',
    desc: '보고서를 쓰기 전 — 논리 설계 · 독자 분석',
    gradient: 'from-indigo-600/20 to-indigo-900/5',
    border: 'border-indigo-700/40',
    header: 'text-indigo-300',
    badge: 'bg-indigo-950/60 text-indigo-300 border-indigo-700/50',
    dot: 'bg-indigo-400',
  },
  작성: {
    label: '작성',
    emoji: '✍️',
    desc: '쓰는 중 — 문서 구조 · 문체 · 시각화',
    gradient: 'from-cyan-600/20 to-cyan-900/5',
    border: 'border-cyan-700/40',
    header: 'text-cyan-300',
    badge: 'bg-cyan-950/60 text-cyan-300 border-cyan-700/50',
    dot: 'bg-cyan-400',
  },
  완성: {
    label: '완성',
    emoji: '✅',
    desc: '마무리 단계 — 검증 · 실전 전략 · 협업',
    gradient: 'from-emerald-600/20 to-emerald-900/5',
    border: 'border-emerald-700/40',
    header: 'text-emerald-300',
    badge: 'bg-emerald-950/60 text-emerald-300 border-emerald-700/50',
    dot: 'bg-emerald-400',
  },
  심화: {
    label: '심화',
    emoji: '🔥',
    desc: '고급 전략 — 심리·설득 · 메타인지',
    gradient: 'from-fuchsia-600/20 to-fuchsia-900/5',
    border: 'border-fuchsia-700/40',
    header: 'text-fuchsia-300',
    badge: 'bg-fuchsia-950/60 text-fuchsia-300 border-fuchsia-700/50',
    dot: 'bg-fuchsia-400',
  },
};
const PHASE_ORDER = ['기획', '작성', '완성', '심화'];

// ── 레벨 설정 ────────────────────────────────────────────────────────────────
const LEVEL_CONFIG = {
  must: {
    label: '필수',
    icon: Flame,
    className: 'bg-red-950/60 text-red-300 border-red-800/60',
    dot: 'bg-red-400',
  },
  recommend: {
    label: '권장',
    icon: Star,
    className: 'bg-amber-950/60 text-amber-300 border-amber-800/60',
    dot: 'bg-amber-400',
  },
  advanced: {
    label: '심화',
    icon: Award,
    className: 'bg-violet-950/60 text-violet-300 border-violet-800/60',
    dot: 'bg-violet-400',
  },
};

// ── 레벨 배지 컴포넌트 ────────────────────────────────────────────────────────
function LevelBadge({ level }) {
  const cfg = LEVEL_CONFIG[level] || LEVEL_CONFIG.recommend;
  return (
    <span className={`flex-shrink-0 inline-flex items-center gap-1 text-[9px] font-bold px-1.5 py-0.5 rounded-full border ${cfg.className}`}>
      <span className={`w-1 h-1 rounded-full ${cfg.dot}`} />
      {cfg.label}
    </span>
  );
}

// ── 카테고리 메타 ────────────────────────────────────────────────────────────
const CATEGORY_META = {
  '핵심 논리': {
    emoji: '🧠',
    desc: 'MECE·BLUF·가설 등 논리 뼈대를 세우는 원칙',
    sectionBg: 'bg-indigo-950/20 border-indigo-700/40',
    header: 'text-indigo-300',
    badge: 'bg-indigo-900/50 text-indigo-300 border-indigo-800',
    iconBg: 'bg-indigo-600/15 text-indigo-400',
    rowHover: 'hover:bg-indigo-950/30 hover:border-indigo-700/30',
    cardRing: 'ring-indigo-500/20',
  },
  '문서 구조': {
    emoji: '🏗️',
    desc: '3단·4단 골격, 제목부터 말미까지 구성하는 프레임',
    sectionBg: 'bg-cyan-950/20 border-cyan-700/40',
    header: 'text-cyan-300',
    badge: 'bg-cyan-900/50 text-cyan-300 border-cyan-800',
    iconBg: 'bg-cyan-600/15 text-cyan-400',
    rowHover: 'hover:bg-cyan-950/30 hover:border-cyan-700/30',
    cardRing: 'ring-cyan-500/20',
  },
  '표현 문체': {
    emoji: '✍️',
    desc: '숫자 중심 서술, 개조식, 용어 통일로 읽히는 글쓰기',
    sectionBg: 'bg-pink-950/20 border-pink-700/40',
    header: 'text-pink-300',
    badge: 'bg-pink-900/50 text-pink-300 border-pink-800',
    iconBg: 'bg-pink-600/15 text-pink-400',
    rowHover: 'hover:bg-pink-950/30 hover:border-pink-700/30',
    cardRing: 'ring-pink-500/20',
  },
  '독자 중심': {
    emoji: '🎯',
    desc: '의사결정권자 중심 설계, 나쁜 소식 전달 전략',
    sectionBg: 'bg-emerald-950/20 border-emerald-700/40',
    header: 'text-emerald-300',
    badge: 'bg-emerald-900/50 text-emerald-300 border-emerald-800',
    iconBg: 'bg-emerald-600/15 text-emerald-400',
    rowHover: 'hover:bg-emerald-950/30 hover:border-emerald-700/30',
    cardRing: 'ring-emerald-500/20',
  },
  '시각화': {
    emoji: '📊',
    desc: '목적에 맞는 차트 선택과 정렬의 미학',
    sectionBg: 'bg-rose-950/20 border-rose-700/40',
    header: 'text-rose-300',
    badge: 'bg-rose-900/50 text-rose-300 border-rose-800',
    iconBg: 'bg-rose-600/15 text-rose-400',
    rowHover: 'hover:bg-rose-950/30 hover:border-rose-700/30',
    cardRing: 'ring-rose-500/20',
  },
  '실전 전략': {
    emoji: '⚡',
    desc: '타이밍, 퇴고, 최악의 패턴 회피 등 현장 기술',
    sectionBg: 'bg-amber-950/20 border-amber-700/40',
    header: 'text-amber-300',
    badge: 'bg-amber-900/50 text-amber-300 border-amber-800',
    iconBg: 'bg-amber-600/15 text-amber-400',
    rowHover: 'hover:bg-amber-950/30 hover:border-amber-700/30',
    cardRing: 'ring-amber-500/20',
  },
  '심리·설득 전략': {
    emoji: '🧲',
    desc: '앵커링, 손실 회피, 사전 줄 세우기 등 설득 심리학 기반 원칙',
    sectionBg: 'bg-violet-950/20 border-violet-700/40',
    header: 'text-violet-300',
    badge: 'bg-violet-900/50 text-violet-300 border-violet-800',
    iconBg: 'bg-violet-600/15 text-violet-400',
    rowHover: 'hover:bg-violet-950/30 hover:border-violet-700/30',
    cardRing: 'ring-violet-500/20',
  },
  '신뢰·검증': {
    emoji: '🛡️',
    desc: '출처 명시, 반론 선제, 현장 교차 검증으로 보고서 신뢰를 완성하는 원칙',
    sectionBg: 'bg-teal-950/20 border-teal-700/40',
    header: 'text-teal-300',
    badge: 'bg-teal-900/50 text-teal-300 border-teal-800',
    iconBg: 'bg-teal-600/15 text-teal-400',
    rowHover: 'hover:bg-teal-950/30 hover:border-teal-700/30',
    cardRing: 'ring-teal-500/20',
  },
  '디지털 실무': {
    emoji: '📱',
    desc: '매체별 최적화, AI 활용, 유효기간 등 요즘 환경에 맞는 실무 원칙',
    sectionBg: 'bg-sky-950/20 border-sky-700/40',
    header: 'text-sky-300',
    badge: 'bg-sky-900/50 text-sky-300 border-sky-800',
    iconBg: 'bg-sky-600/15 text-sky-400',
    rowHover: 'hover:bg-sky-950/30 hover:border-sky-700/30',
    cardRing: 'ring-sky-500/20',
  },
  '협업·프로세스': {
    emoji: '🔗',
    desc: '버전 관리, 피드백 기록, 공동 작성 룰로 팀 보고서를 완성하는 원칙',
    sectionBg: 'bg-orange-950/20 border-orange-700/40',
    header: 'text-orange-300',
    badge: 'bg-orange-900/50 text-orange-300 border-orange-800',
    iconBg: 'bg-orange-600/15 text-orange-400',
    rowHover: 'hover:bg-orange-950/30 hover:border-orange-700/30',
    cardRing: 'ring-orange-500/20',
  },
  '메타인지': {
    emoji: '🧭',
    desc: '확증 편향 경계, 숫자 맥락화, 불확실성 명시로 분석의 품격을 높이는 원칙',
    sectionBg: 'bg-fuchsia-950/20 border-fuchsia-700/40',
    header: 'text-fuchsia-300',
    badge: 'bg-fuchsia-900/50 text-fuchsia-300 border-fuchsia-800',
    iconBg: 'bg-fuchsia-600/15 text-fuchsia-400',
    rowHover: 'hover:bg-fuchsia-950/30 hover:border-fuchsia-700/30',
    cardRing: 'ring-fuchsia-500/20',
  },
};

// ── Before / After 비교 블록 ─────────────────────────────────────────────────
function BeforeAfterBlock({ principle }) {
  return (
    <motion.div
      key="ba"
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: 'auto' }}
      exit={{ opacity: 0, height: 0 }}
      transition={{ duration: 0.28, ease: 'easeInOut' }}
      className="overflow-hidden"
    >
      <div className="pt-0 pb-3 px-4">

        {/* 요약 설명 한 줄 */}
        <p className="text-xs text-slate-500 mb-2 leading-relaxed">
          {principle.description}
        </p>

        {/* 🧠 기억법 (memo) */}
        {principle.memo && (
          <div className="flex items-start gap-2 mb-3 px-3 py-2 rounded-lg bg-amber-950/30 border border-amber-700/30">
            <span className="text-sm flex-shrink-0">🧠</span>
            <p className="text-[11px] text-amber-300/90 leading-relaxed font-medium">
              {principle.memo}
            </p>
          </div>
        )}

        {/* Before / After 비교 */}
        <div className="grid grid-cols-2 gap-2">
          {/* ❌ Before */}
          <div className="rounded-xl border border-red-900/50 bg-red-950/20 p-3">
            <div className="flex items-center gap-1.5 mb-2">
              <XIcon size={12} className="text-red-400 flex-shrink-0" />
              <span className="text-[10px] font-bold text-red-400 uppercase tracking-wider">
                {principle.beforeLabel || 'Before'}
              </span>
            </div>
            <pre className="text-[11px] text-red-200/70 font-mono leading-relaxed whitespace-pre-wrap">
              {principle.before}
            </pre>
          </div>

          {/* ✅ After */}
          <div className="rounded-xl border border-emerald-900/50 bg-emerald-950/20 p-3">
            <div className="flex items-center gap-1.5 mb-2">
              <CheckCircle2 size={12} className="text-emerald-400 flex-shrink-0" />
              <span className="text-[10px] font-bold text-emerald-400 uppercase tracking-wider">
                {principle.afterLabel || 'After'}
              </span>
            </div>
            <pre className="text-[11px] text-emerald-200/80 font-mono leading-relaxed whitespace-pre-wrap">
              {principle.after}
            </pre>
          </div>
        </div>

        {/* 심화 힌트 (있으면) */}
        {principle.detail && (
          <p className="mt-2.5 text-[11px] text-slate-600 leading-relaxed pl-1 border-l-2 border-slate-800">
            💡 {principle.detail}
          </p>
        )}

        {/* 📱 MZ 꿀팁 (있으면) */}
        {principle.mzTip && (
          <div className="mt-3 flex items-start gap-2 px-3 py-2.5 rounded-xl bg-gradient-to-r from-violet-950/60 to-fuchsia-950/40 border border-violet-700/40">
            <span className="text-sm flex-shrink-0">📱</span>
            <div>
              <span className="text-[9px] font-black uppercase tracking-widest text-violet-400 mb-0.5 block">MZ 꿀팁</span>
              <p className="text-[11px] text-violet-200/90 leading-relaxed">{principle.mzTip}</p>
            </div>
          </div>
        )}

      </div>
    </motion.div>
  );
}

// ── 원칙 행 (클릭 시 Before/After 인라인 확장) ──────────────────────────────
function PrincipleRow({ principle, meta, isExpanded, onToggle, checklistMode, isChecked, onCheck, displayIndex, isFav, onFavToggle }) {
  const IconComp = ICON_MAP[principle.icon] || Network;

  return (
    <div className={`
      rounded-xl border transition-all duration-200
      ${isExpanded
        ? `border-slate-700/60 bg-slate-800/50 ring-1 ${meta?.cardRing}`
        : `border-slate-800/60 bg-slate-900/30 ${meta?.rowHover} hover:border-slate-700/40`
      }
      ${checklistMode && isChecked ? 'opacity-50' : ''}
    `}>
      {/* 헤더 행 — 행 전체가 클릭 영역 */}
      <button
        onClick={checklistMode ? undefined : onToggle}
        className="w-full flex items-center gap-3 px-4 py-3 text-left"
        style={{ cursor: checklistMode ? 'default' : 'pointer' }}
      >
        {/* 체크리스트 모드: 체크박스 */}
        {checklistMode && (
          <span
            role="button"
            onClick={(e) => { e.stopPropagation(); onCheck(principle.id); }}
            className="flex-shrink-0 transition-colors"
          >
            {isChecked
              ? <CheckSquare size={16} className="text-emerald-400" />
              : <Square size={16} className="text-slate-600 hover:text-slate-400" />}
          </span>
        )}

        {/* 원칙 번호 배지 */}
        <div className={`flex-shrink-0 w-7 h-7 rounded-lg flex items-center justify-center text-[11px] font-bold ${meta?.iconBg} tabular-nums`}>
          {displayIndex ?? principle.id}
        </div>

        {/* 제목 + 부제 + 레벨 배지 */}
        <div className="flex-1 min-w-0 text-left">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`font-semibold text-sm text-white ${isExpanded ? 'text-indigo-100' : ''}`}>
              {principle.title}
            </span>
            <LevelBadge level={principle.level} />
            {/* MZ 꿀팁 있는 원칙에 작은 표시 */}
            {principle.mzTip && (
              <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-violet-900/60 border border-violet-700/50 text-violet-400 font-semibold flex-shrink-0">
                📱
              </span>
            )}
          </div>
          <span className="text-slate-600 text-xs">{principle.subtitle}</span>
        </div>

        {/* ⭐ 즐겨찾기 버튼 — 이벤트 버블링 막아야 행 클릭과 분리됨 */}
        <span
          role="button"
          onClick={(e) => { e.stopPropagation(); onFavToggle(principle.id); }}
          className={`flex-shrink-0 p-1 rounded-lg transition-all duration-150 ${
            isFav
              ? 'text-amber-400 hover:text-amber-300'
              : 'text-slate-700 hover:text-amber-500'
          }`}
          title={isFav ? '즐겨찾기 해제' : '즐겨찾기 추가'}
        >
          <Star size={14} fill={isFav ? 'currentColor' : 'none'} />
        </span>

        {/* 펼치기 힌트 */}
        {!isExpanded && !checklistMode && (
          <span className="flex-shrink-0 text-[9px] text-slate-700 mr-1 hidden sm:block">예시 보기</span>
        )}

        {/* 화살표 (체크리스트 모드에선 숨김) */}
        {!checklistMode && (
          <motion.div
            animate={{ rotate: isExpanded ? 90 : 0 }}
            transition={{ duration: 0.2 }}
            className="flex-shrink-0"
          >
            <ChevronRight size={14} className={isExpanded ? 'text-slate-400' : 'text-slate-700'} />
          </motion.div>
        )}
      </button>

      {/* Before / After 펼침 영역 (체크리스트 모드에선 숨김) */}
      {!checklistMode && (
        <AnimatePresence>
          {isExpanded && <BeforeAfterBlock principle={principle} />}
        </AnimatePresence>
      )}
    </div>
  );
}

// ── 아코디언 섹션 ─────────────────────────────────────────────────────────────
function AccordionSection({ category, principles, isSectionOpen, onSectionToggle, expandedId, onRowToggle, checklistMode, checkedIds, onCheck, sectionIndex, displayIndexMap }) {
  const meta = CATEGORY_META[category];
  const count = principles.length;

  return (
    <div className={`
      rounded-2xl border overflow-hidden transition-all duration-300
      ${isSectionOpen
        ? `${meta?.sectionBg}`
        : 'border-slate-800/60 bg-slate-900/20 hover:border-slate-700/40'}
    `}>
      {/* 섹션 헤더 */}
      <button
        onClick={onSectionToggle}
        className="w-full flex items-center gap-4 px-5 py-4 text-left"
      >
        {/* 영역 번호 배지 */}
        <span className="flex-shrink-0 w-7 h-7 rounded-full bg-slate-800/80 border border-slate-700/60 flex items-center justify-center text-[11px] font-bold text-slate-500">
          {sectionIndex}
        </span>

        <span className="text-xl flex-shrink-0 leading-none">{meta?.emoji}</span>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className={`font-bold text-[15px] ${isSectionOpen ? meta?.header : 'text-slate-300'} transition-colors`}>
              {category}
            </span>
            <span className={`
              flex-shrink-0 text-[10px] font-bold px-2 py-0.5 rounded-full border
              ${isSectionOpen ? meta?.badge : 'bg-slate-800 text-slate-500 border-slate-700'}
              transition-all
            `}>
              {count}가지
            </span>
          </div>
          <AnimatePresence>
            {!isSectionOpen && (
              <motion.p
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="text-[11px] text-slate-600 mt-0.5 truncate"
              >
                {meta?.desc}
              </motion.p>
            )}
          </AnimatePresence>
        </div>

        <motion.div animate={{ rotate: isSectionOpen ? 180 : 0 }} transition={{ duration: 0.25 }}>
          <ChevronDown size={17} className={`${isSectionOpen ? meta?.header : 'text-slate-600'} transition-colors`} />
        </motion.div>
      </button>

      {/* 원칙 목록 */}
      <AnimatePresence>
        {isSectionOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.28, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            {/* 카테고리 설명 */}
            <div className="px-5 pb-2">
              <p className="text-[11px] text-slate-500">{meta?.desc}</p>
            </div>
            <div className="mx-5 mb-3 h-px bg-slate-800/50" />

            {/* 원칙 rows */}
            <div className="px-5 pb-5 space-y-2">
              {principles.map((p) => (
                <PrincipleRow
                  key={p.id}
                  principle={p}
                  meta={meta}
                  isExpanded={expandedId === p.id}
                  onToggle={() => onRowToggle(p.id)}
                  checklistMode={checklistMode}
                  isChecked={checkedIds.has(p.id)}
                  onCheck={onCheck}
                  displayIndex={displayIndexMap?.[p.id]}
                />
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ── 오늘의 원칙 위젯 ─────────────────────────────────────────────────────────
function TodaysPrinciple({ onRefresh }) {
  const MUST_PRINCIPLES = AUGMENTED_PRINCIPLES.filter(p => p.level === 'must');
  const [idx, setIdx] = useState(() =>
    Math.floor(Date.now() / 86400000) % MUST_PRINCIPLES.length
  );
  const principle = MUST_PRINCIPLES[idx];

  const handleRefresh = () => {
    setIdx(prev => (prev + 1) % MUST_PRINCIPLES.length);
  };

  if (!principle) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.1 }}
      className="max-w-2xl mx-auto px-4 mb-4"
    >
      <div className="rounded-2xl border border-amber-800/40 bg-amber-950/20 p-4">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Flame size={13} className="text-amber-400" />
            <span className="text-[11px] font-bold text-amber-400 tracking-wider uppercase">오늘의 원칙</span>
          </div>
          <button
            onClick={handleRefresh}
            className="p-1 rounded-lg hover:bg-amber-900/30 transition-colors"
            title="다른 원칙 보기"
          >
            <RefreshCw size={11} className="text-amber-700 hover:text-amber-500 transition-colors" />
          </button>
        </div>
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 w-8 h-8 rounded-xl bg-amber-900/40 flex items-center justify-center text-amber-400 text-sm font-bold">
            {principle.id}
          </div>
          <div className="min-w-0">
            <p className="font-bold text-sm text-amber-100">{principle.title}</p>
            <p className="text-xs text-amber-300/60 mt-0.5 line-clamp-2">{principle.subtitle}</p>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

// ── 레벨 필터 탭 ─────────────────────────────────────────────────────────────
const LEVEL_TABS = [
  { key: 'all', label: '전체', count: null },
  { key: 'must', label: '🔥 필수', count: null },
  { key: 'recommend', label: '⭐ 권장', count: null },
  { key: 'advanced', label: '🏆 심화', count: null },
];

// ── 메인 대시보드 ────────────────────────────────────────────────────────────
export default function GuidelinesDashboard() {
  // 즐겨찾기 후크 — localStorage에 지속 저장
  const { favSet, toggle: toggleFav, isFav, count: favCount } = useGuidelineFavorites();

  const [openCategory, setOpenCategory] = useState(null); // 초기엔 모두 닫힌 상태
  const [openPhase, setOpenPhase] = useState(null);       // 단계별 모드 열린 phase — 초기 닫힘
  const [openTarget, setOpenTarget] = useState(null);     // 독자별 열린 그룹 — 초기 닫힘
  const [openSituation, setOpenSituation] = useState(null); // 상황별 열린 그룹 — 초기 닫힘
  const [openLevel, setOpenLevel] = useState(null);       // 난이도별 열린 그룹 — 초기 닫힘
  const [expandedId, setExpandedId] = useState(null);
  const [levelFilter, setLevelFilter] = useState('all');
  const [viewMode, setViewMode] = useState('phase'); // 'phase' | 'category' | 'fav'
  const [checklistMode, setChecklistMode] = useState(false);
  const [checkedIds, setCheckedIds] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState('');

  const categoryOrder = useMemo(
    () => CATEGORIES.filter((c) => c.id !== 'all').map((c) => c.id),
    []
  );

  // 검색어 필터링 적용된 원칙 목록
  const filteredAugmented = useMemo(() => {
    if (!searchQuery.trim()) return AUGMENTED_PRINCIPLES;
    const q = searchQuery.toLowerCase();
    return AUGMENTED_PRINCIPLES.filter((p) => 
      p.title.toLowerCase().includes(q) ||
      (p.subtitle && p.subtitle.toLowerCase().includes(q)) ||
      (p.before && p.before.toLowerCase().includes(q)) ||
      (p.after && p.after.toLowerCase().includes(q)) ||
      (p.memo && p.memo.toLowerCase().includes(q))
    );
  }, [searchQuery]);

  // 레벨 필터 적용 후 카테고리별 그룹
  const grouped = useMemo(() => {
    const map = {};
    categoryOrder.forEach((cat) => {
      map[cat] = filteredAugmented.filter(
        (p) =>
          p.category === cat &&
          (levelFilter === 'all' || p.level === levelFilter)
      );
    });
    return map;
  }, [categoryOrder, levelFilter, filteredAugmented]);

  // 단계별 그룹 (phase 모드용)
  const groupedByPhase = useMemo(() => {
    const map = {};
    PHASE_ORDER.forEach((ph) => {
      map[ph] = filteredAugmented.filter(
        (p) =>
          p.phase === ph &&
          (levelFilter === 'all' || p.level === levelFilter)
      );
    });
    return map;
  }, [levelFilter, filteredAugmented]);

  // 카테고리 순으로 표시 번호 부여
  const displayIndexMap = useMemo(() => {
    const map = {};
    let counter = 1;
    categoryOrder.forEach((cat) => {
      filteredAugmented
        .filter((p) => p.category === cat)
        .forEach((p) => { map[p.id] = counter++; });
    });
    return map;
  }, [categoryOrder, filteredAugmented]);

  // 단계별(phase) 순으로 표시 번호 부여 — 기획 1번부터 시작
  const phaseIndexMap = useMemo(() => {
    const map = {};
    let counter = 1;
    PHASE_ORDER.forEach((ph) => {
      filteredAugmented
        .filter((p) => p.phase === ph)
        .forEach((p) => { map[p.id] = counter++; });
    });
    return map;
  }, [filteredAugmented]);

  // 레벨별 카운트 (이것은 전체 개수들을 보여주기 위해 AUGMENTED_PRINCIPLES 유지)
  const levelCounts = useMemo(() => ({
    all: AUGMENTED_PRINCIPLES.length,
    must: AUGMENTED_PRINCIPLES.filter(p => p.level === 'must').length,
    recommend: AUGMENTED_PRINCIPLES.filter(p => p.level === 'recommend').length,
    advanced: AUGMENTED_PRINCIPLES.filter(p => p.level === 'advanced').length,
  }), []);

  const mzCount = useMemo(() => AUGMENTED_PRINCIPLES.filter(p => !!p.mzTip).length, []);

  // ── 새로운 그룹화 로직 (target, situation, level, checklist) ──
  const groupedByTarget = useMemo(() => {
    const map = {};
    filteredAugmented.forEach(p => {
      if (!p.targets) return;
      if (levelFilter !== 'all' && p.level !== levelFilter) return;
      p.targets.forEach(t => {
        if (!map[t]) map[t] = [];
        map[t].push(p);
      });
    });
    return map;
  }, [levelFilter, filteredAugmented]);
  const targetOrders = useMemo(() => Object.keys(groupedByTarget).sort(), [groupedByTarget]);

  const groupedBySituation = useMemo(() => {
    const map = {};
    filteredAugmented.forEach(p => {
      if (!p.situations) return;
      if (levelFilter !== 'all' && p.level !== levelFilter) return;
      p.situations.forEach(s => {
        if (!map[s]) map[s] = [];
        map[s].push(p);
      });
    });
    return map;
  }, [levelFilter, filteredAugmented]);
  const situationOrders = useMemo(() => Object.keys(groupedBySituation).sort(), [groupedBySituation]);

  // 난이도별 뷰는 레벨 필터와 역할이 같으므로 레벨 필터 무시하고 항상 3그룹 전체 표시
  const groupedByLevel = useMemo(() => {
    const map = { must: [], recommend: [], advanced: [] };
    filteredAugmented.forEach(p => {
      map[p.level].push(p);
    });
    return map;
  }, [filteredAugmented]);
  const levelOrders = ['must', 'recommend', 'advanced'];

  const checklistPrinciples = useMemo(() => {
    return filteredAugmented.filter((p) => p.isChecklist && (levelFilter === 'all' || p.level === levelFilter));
  }, [levelFilter, filteredAugmented]);

  const handleSectionToggle = (cat) => {
    setOpenCategory((prev) => (prev === cat ? null : cat));
    setExpandedId(null);
  };

  const handlePhaseToggle = (ph) => {
    setOpenPhase((prev) => (prev === ph ? null : ph));
    setExpandedId(null);
  };

  const handleRowToggle = (id) => {
    if (checklistMode) return;
    setExpandedId((prev) => (prev === id ? null : id));
  };

  const handleCheck = (id) => {
    setCheckedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleLevelFilter = (key) => {
    setLevelFilter(key);
    setExpandedId(null);
  };

  const handleViewMode = (mode) => {
    setViewMode(mode);
    setExpandedId(null);
    // 난이도별 뷰는 자체 필수/권장/심화 그룹이 있으므로 레벨 필터를 전체로 리셋
    if (mode === 'level') {
      setLevelFilter('all');
    }
    if (mode === 'checklist') {
      setChecklistMode(true);
    } else {
      setChecklistMode(false);
    }
  };

  // 즉즐찾기 목록 (레벨 필터 적용)
  const favPrinciples = useMemo(() =>
    filteredAugmented.filter(
      (p) => favSet.has(p.id) && (levelFilter === 'all' || p.level === levelFilter)
    ),
  [favSet, levelFilter, filteredAugmented]);

  // 현재 필터 조건의 총 원칙 수 (viewMode에 따라 다름)
  const filteredTotal = useMemo(() => {
    switch (viewMode) {
      case 'phase': return Object.values(groupedByPhase).reduce((a, b) => a + b.length, 0);
      case 'category': return Object.values(grouped).reduce((a, b) => a + b.length, 0);
      case 'target': return Object.values(groupedByTarget).reduce((a, b) => a + b.length, 0);
      case 'situation': return Object.values(groupedBySituation).reduce((a, b) => a + b.length, 0);
      case 'level': return Object.values(groupedByLevel).reduce((a, b) => a + b.length, 0);
      case 'checklist': return checklistPrinciples.length;
      case 'fav': return favPrinciples.length;
      default: return 0;
    }
  }, [viewMode, groupedByPhase, grouped, groupedByTarget, groupedBySituation, groupedByLevel, checklistPrinciples, favPrinciples]);

  return (
    <div className="min-h-screen bg-[#0A0F1C]">

      {/* ── Hero ─────────────────────────────────────────────── */}
      <div className="relative overflow-hidden pt-14 pb-6 px-4">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-20 left-1/2 -translate-x-1/2 w-[480px] h-[220px] bg-indigo-600/8 rounded-full blur-3xl" />
        </div>
        <div className="relative max-w-2xl mx-auto text-center">
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-indigo-800/60 bg-indigo-950/40 text-indigo-400 text-xs font-medium mb-4">
              <span>✦</span>
              <span>McKinsey 원칙 + 대통령 비서실 매뉴얼 + 심리·설득 전략 통합</span>
            </div>
            <h1 className="text-3xl font-extrabold text-white leading-tight tracking-tight mb-2">
              보고서 핵심 원칙{' '}
              <span className="bg-gradient-to-r from-indigo-400 to-violet-400 bg-clip-text text-transparent">
                {levelCounts.all}가지
              </span>
            </h1>
            <p className="text-slate-500 text-sm">
              원칙을 클릭하면 <span className="text-slate-400 font-medium">❌ Before / ✅ After</span> 예시로 바로 이해할 수 있어요
            </p>
          </motion.div>
        </div>
      </div>

      {/* ── 오늘의 원칙 위젯 ──────────────────────────────────── */}
      <TodaysPrinciple />

      {/* ── 툴바: 검색 + 레벨 필터 + 뷰 모드 + 체크 ─────── */}
      <div className="max-w-2xl mx-auto px-4 mb-4 space-y-3">
        {/* 검색 바 */}
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
            <Search size={16} className="text-slate-500" />
          </div>
          <input
            type="text"
            className="block w-full pl-11 pr-10 py-3 bg-slate-900/40 border border-slate-800/60 rounded-xl text-sm text-slate-200 placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-indigo-500/50 focus:border-indigo-500/50 transition-all font-medium backdrop-blur-sm"
            placeholder="제목, 예시 내용, 키워드로 원칙 검색..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-500 hover:text-slate-300 transition-colors"
            >
              <div className="bg-slate-800 rounded-full p-1">
                <XIcon size={14} />
              </div>
            </button>
          )}
        </div>

        {/* 1행: 레벨 탭 + 체크리스트 (난이도별 뷰에선 레벨 필터 숨김 — 역할이 겹치므로) */}
        {viewMode !== 'level' && (
          <div className="flex items-center gap-2">
            <div className="flex gap-1 flex-1 bg-slate-900/50 border border-slate-800/60 rounded-xl p-1">
              {LEVEL_TABS.map(tab => (
                <button
                  key={tab.key}
                  onClick={() => handleLevelFilter(tab.key)}
                  className={`flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-lg text-xs font-semibold transition-all duration-200 ${
                    levelFilter === tab.key ? 'bg-white/10 text-white shadow-sm' : 'text-slate-500 hover:text-slate-300'
                  }`}
                >
                  {tab.label}
                  <span className={`text-[9px] px-1.5 py-0.5 rounded-full ${
                    levelFilter === tab.key ? 'bg-white/10 text-white' : 'bg-slate-800 text-slate-600'
                  }`}>
                    {levelCounts[tab.key]}
                  </span>
                </button>
              ))}
            </div>
            <button
              onClick={() => { setChecklistMode(p => !p); setCheckedIds(new Set()); }}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-xl border text-xs font-semibold transition-all duration-200 ${
                checklistMode
                  ? 'bg-emerald-950/60 border-emerald-700/60 text-emerald-300'
                  : 'bg-slate-900/50 border-slate-800/60 text-slate-500 hover:text-slate-300 hover:border-slate-700'
              }`}
              title="체크리스트 모드"
            >
              <CheckSquare size={13} />
              <span className="hidden sm:inline">체크</span>
            </button>
          </div>
        )}

        {/* 2행: 가로 스크롤 가능형 뷰 모드 표기 칩(Chip) 탭 */}
        <div className="flex overflow-x-auto gap-1.5 pb-2 -mx-4 px-4 sm:mx-0 sm:px-0 scrollbar-hide">
          {VIEW_MODES.map(mode => {
            const Icon = mode.icon;
            const isActive = viewMode === mode.id;
            const count = mode.id === 'fav' && favCount > 0 ? favCount : null;

            return (
              <button
                key={mode.id}
                onClick={() => handleViewMode(mode.id)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border whitespace-nowrap text-[11px] sm:text-xs font-semibold transition-all duration-200 flex-shrink-0 ${
                  mode.id === 'fav'
                    ? (isActive 
                        ? 'bg-amber-500/20 text-amber-300 border-amber-500/50' 
                        : 'bg-slate-900/50 text-slate-500 border-slate-800/60 hover:text-slate-300')
                    : (isActive 
                        ? 'bg-indigo-600 border-indigo-500 text-white shadow-lg shadow-indigo-900/40' 
                        : 'bg-slate-900/50 text-slate-500 border-slate-800/60 hover:text-slate-300 hover:border-slate-700/60')
                }`}
              >
                <Icon size={12} fill={mode.id === 'fav' && isActive ? 'currentColor' : 'none'} />
                {mode.label}
                {count !== null && (
                  <span className={`text-[9px] px-1.5 py-0.5 rounded-full ${isActive ? 'bg-amber-500/30 text-amber-200' : 'bg-slate-800 text-slate-500'}`}>
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* 체크리스트 모드 진행률 */}
        <AnimatePresence>
          {checklistMode && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="flex items-center gap-3"
            >
              <div className="flex-1 h-1.5 bg-slate-800 rounded-full overflow-hidden">
                <motion.div
                  className="h-full bg-emerald-500 rounded-full"
                  initial={{ width: 0 }}
                  animate={{ width: `${filteredTotal > 0 ? (checkedIds.size / filteredTotal) * 100 : 0}%` }}
                  transition={{ duration: 0.4 }}
                />
              </div>
              <span className="text-[11px] text-slate-500 flex-shrink-0">
                {checkedIds.size}/{filteredTotal} 완료
              </span>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* ── 아코디언 ──────────────────────────────────────────── */}
      <div className="max-w-2xl mx-auto px-4 pb-16 space-y-3">

        {/* ── 카테고리 보기 ── */}
        {viewMode === 'category' && categoryOrder.map((cat, idx) => {
          const pList = grouped[cat] || [];
          if (pList.length === 0) return null;
          return (
            <motion.div
              key={cat}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: idx * 0.04 }}
            >
              <AccordionSection
                category={cat}
                principles={pList}
                isSectionOpen={openCategory === cat}
                onSectionToggle={() => handleSectionToggle(cat)}
                expandedId={expandedId}
                onRowToggle={handleRowToggle}
                checklistMode={checklistMode}
                checkedIds={checkedIds}
                onCheck={handleCheck}
                sectionIndex={idx + 1}
                displayIndexMap={displayIndexMap}
                isFav={isFav}
                onFavToggle={toggleFav}
              />
            </motion.div>
          );
        })}

        {/* ── 독자(타겟)별 보기 — 아코디언, 초기 닫힘 ── */}
        {viewMode === 'target' && targetOrders.map((tGroup, idx) => {
          const pList = groupedByTarget[tGroup] || [];
          if (pList.length === 0) return null;
          const isOpen = openTarget === tGroup;
          return (
            <motion.div key={tGroup} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: idx * 0.04 }}>
              <div className={`rounded-2xl border overflow-hidden mb-3 transition-all duration-300 ${
                isOpen ? 'border-indigo-700/40 bg-indigo-950/10' : 'border-slate-800/60 bg-slate-900/20 hover:border-slate-700/40'
              }`}>
                {/* 헤더 — 클릭 시 토글 */}
                <button
                  onClick={() => { setOpenTarget(p => p === tGroup ? null : tGroup); setExpandedId(null); }}
                  className="w-full px-5 py-4 flex items-center gap-3 text-left"
                >
                  <Users size={16} className={isOpen ? 'text-indigo-400' : 'text-slate-500'} />
                  <span className={`font-bold text-[15px] flex-1 ${isOpen ? 'text-indigo-200' : 'text-slate-300'}`}>{tGroup} 대상</span>
                  <span className="text-[10px] bg-slate-800 text-slate-400 px-2 py-0.5 rounded-full border border-slate-700/60 mr-1">{pList.length}가지</span>
                  <motion.div animate={{ rotate: isOpen ? 180 : 0 }} transition={{ duration: 0.25 }}>
                    <ChevronDown size={16} className={isOpen ? 'text-indigo-400' : 'text-slate-600'} />
                  </motion.div>
                </button>
                {/* 원칙 목록 — 아코디언 펼침 */}
                <AnimatePresence>
                  {isOpen && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.28, ease: 'easeInOut' }}
                      className="overflow-hidden"
                    >
                      <div className="mx-5 mb-2 h-px bg-slate-800/50" />
                      <div className="px-5 pb-5 space-y-2">
                        {pList.map((p) => (
                          <PrincipleRow
                            key={`${tGroup}-${p.id}`} principle={p} meta={CATEGORY_META[p.category]}
                            isExpanded={expandedId === p.id} onToggle={() => handleRowToggle(p.id)}
                            checklistMode={checklistMode} isChecked={checkedIds.has(p.id)} onCheck={handleCheck}
                            isFav={isFav(p.id)} onFavToggle={toggleFav}
                          />
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </motion.div>
          );
        })}

        {/* ── 상황별 보기 — 아코디언, 초기 닫힘 ── */}
        {viewMode === 'situation' && situationOrders.map((sGroup, idx) => {
          const pList = groupedBySituation[sGroup] || [];
          if (pList.length === 0) return null;
          const isOpen = openSituation === sGroup;
          return (
            <motion.div key={sGroup} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: idx * 0.04 }}>
              <div className={`rounded-2xl border overflow-hidden mb-3 transition-all duration-300 ${
                isOpen ? 'border-cyan-700/40 bg-cyan-950/10' : 'border-slate-800/60 bg-slate-900/20 hover:border-slate-700/40'
              }`}>
                <button
                  onClick={() => { setOpenSituation(p => p === sGroup ? null : sGroup); setExpandedId(null); }}
                  className="w-full px-5 py-4 flex items-center gap-3 text-left"
                >
                  <Activity size={16} className={isOpen ? 'text-cyan-400' : 'text-slate-500'} />
                  <span className={`font-bold text-[15px] flex-1 ${isOpen ? 'text-cyan-200' : 'text-slate-300'}`}>{sGroup} 상황</span>
                  <span className="text-[10px] bg-slate-800 text-slate-400 px-2 py-0.5 rounded-full border border-slate-700/60 mr-1">{pList.length}가지</span>
                  <motion.div animate={{ rotate: isOpen ? 180 : 0 }} transition={{ duration: 0.25 }}>
                    <ChevronDown size={16} className={isOpen ? 'text-cyan-400' : 'text-slate-600'} />
                  </motion.div>
                </button>
                <AnimatePresence>
                  {isOpen && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.28, ease: 'easeInOut' }}
                      className="overflow-hidden"
                    >
                      <div className="mx-5 mb-2 h-px bg-slate-800/50" />
                      <div className="px-5 pb-5 space-y-2">
                        {pList.map((p) => (
                          <PrincipleRow
                            key={`${sGroup}-${p.id}`} principle={p} meta={CATEGORY_META[p.category]}
                            isExpanded={expandedId === p.id} onToggle={() => handleRowToggle(p.id)}
                            checklistMode={checklistMode} isChecked={checkedIds.has(p.id)} onCheck={handleCheck}
                            isFav={isFav(p.id)} onFavToggle={toggleFav}
                          />
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </motion.div>
          );
        })}

        {/* ── 난이도별 보기 — 아코디언, 초기 닫힘, 레벨 필터 미적용 ── */}
        {viewMode === 'level' && levelOrders.map((lGroup, idx) => {
          const pList = groupedByLevel[lGroup] || [];
          if (pList.length === 0) return null;
          const cfg = LEVEL_CONFIG[lGroup];
          const Icon = cfg.icon;
          const isOpen = openLevel === lGroup;
          // 레벨에 따른 색상 매핑
          const colorMap = {
            must:      { border: 'border-red-700/40',    bg: 'bg-red-950/10',    text: 'text-red-200',    icon: 'text-red-400',    divider: 'bg-red-900/30',    chevron: 'text-red-400' },
            recommend: { border: 'border-amber-700/40',  bg: 'bg-amber-950/10',  text: 'text-amber-200',  icon: 'text-amber-400',  divider: 'bg-amber-900/30',  chevron: 'text-amber-400' },
            advanced:  { border: 'border-violet-700/40', bg: 'bg-violet-950/10', text: 'text-violet-200', icon: 'text-violet-400', divider: 'bg-violet-900/30', chevron: 'text-violet-400' },
          };
          const colors = colorMap[lGroup];
          return (
            <motion.div key={lGroup} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: idx * 0.04 }}>
              <div className={`rounded-2xl border overflow-hidden mb-3 transition-all duration-300 ${
                isOpen ? `${colors.border} ${colors.bg}` : 'border-slate-800/60 bg-slate-900/20 hover:border-slate-700/40'
              }`}>
                <button
                  onClick={() => { setOpenLevel(p => p === lGroup ? null : lGroup); setExpandedId(null); }}
                  className="w-full px-5 py-4 flex items-center gap-3 text-left"
                >
                  <Icon size={16} className={isOpen ? colors.icon : 'text-slate-500'} />
                  <span className={`font-bold text-[15px] flex-1 ${isOpen ? colors.text : 'text-slate-300'}`}>{cfg.label}</span>
                  <span className="text-[10px] bg-slate-800 text-slate-400 px-2 py-0.5 rounded-full border border-slate-700/60 mr-1">{pList.length}가지</span>
                  <motion.div animate={{ rotate: isOpen ? 180 : 0 }} transition={{ duration: 0.25 }}>
                    <ChevronDown size={16} className={isOpen ? colors.chevron : 'text-slate-600'} />
                  </motion.div>
                </button>
                <AnimatePresence>
                  {isOpen && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.28, ease: 'easeInOut' }}
                      className="overflow-hidden"
                    >
                      <div className={`mx-5 mb-2 h-px ${colors.divider}`} />
                      <div className="px-5 pb-5 space-y-2">
                        {pList.map((p) => (
                          <PrincipleRow
                            key={`${lGroup}-${p.id}`} principle={p} meta={CATEGORY_META[p.category]}
                            isExpanded={expandedId === p.id} onToggle={() => handleRowToggle(p.id)}
                            checklistMode={checklistMode} isChecked={checkedIds.has(p.id)} onCheck={handleCheck}
                            isFav={isFav(p.id)} onFavToggle={toggleFav}
                          />
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </motion.div>
          );
        })}

        {/* ── 자가진단(체크리스트) 모드 보기 ── */}
        {viewMode === 'checklist' && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
             <div className="rounded-2xl border border-emerald-900/40 bg-emerald-950/20 overflow-hidden mb-3">
                <div className="px-5 py-4 border-b border-emerald-900/40 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <CheckSquare size={16} className="text-emerald-400" />
                    <span className="font-bold text-[15px] text-emerald-200">5분 자가진단표 (제출 전 확인)</span>
                    <span className="text-[10px] bg-emerald-950/60 text-emerald-400 px-2 py-0.5 rounded-full border border-emerald-800/60">{checklistPrinciples.length}항목</span>
                  </div>
                </div>
                <div className="px-5 pb-5 pt-3 space-y-2">
                  {checklistPrinciples.map((p) => (
                    <PrincipleRow
                      key={`checklist-${p.id}`} principle={p} meta={CATEGORY_META[p.category]}
                      isExpanded={false} onToggle={() => {}}
                      checklistMode={true} isChecked={checkedIds.has(p.id)} onCheck={handleCheck}
                      isFav={isFav(p.id)} onFavToggle={toggleFav}
                    />
                  ))}
                  {checklistPrinciples.length === 0 && (
                    <p className="text-sm text-center text-slate-500 py-10">해당 필터에 맞는 체크리스트가 없습니다.</p>
                  )}
                </div>
              </div>
          </motion.div>
        )}

        {/* ── 단계별 보기 ── */}
        {viewMode === 'phase' && PHASE_ORDER.map((ph, idx) => {
          const pList = groupedByPhase[ph] || [];
          if (pList.length === 0) return null;
          const phCfg = PHASE_CONFIG[ph];
          return (
            <motion.div
              key={ph}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: idx * 0.06 }}
              className={`rounded-2xl border bg-gradient-to-b ${phCfg.gradient} ${phCfg.border}`}
            >
              {/* Phase 헤더 */}
              <button
                className="w-full flex items-center gap-3 px-5 py-4 text-left"
                onClick={() => handlePhaseToggle(ph)}
              >
                <span className="text-2xl">{phCfg.emoji}</span>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className={`font-bold text-[15px] ${phCfg.header}`}>
                      {phCfg.label} 단계
                    </span>
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${phCfg.badge}`}>
                      {pList.length}가지
                    </span>
                  </div>
                  <p className="text-[11px] text-slate-500 mt-0.5">{phCfg.desc}</p>
                </div>
                <motion.div animate={{ rotate: openPhase === ph ? 180 : 0 }} transition={{ duration: 0.25 }}>
                  <ChevronDown size={17} className={phCfg.header} />
                </motion.div>
              </button>

              {/* Phase 원칙 목록 */}
              <AnimatePresence>
                {openPhase === ph && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.28 }}
                    className="overflow-hidden"
                  >
                    <div className="px-5 pb-5 space-y-2">
                      {pList.map((p) => {
                        const catMeta = CATEGORY_META[p.category];
                        return (
                          <PrincipleRow
                            key={p.id}
                            principle={p}
                            meta={catMeta}
                            isExpanded={expandedId === p.id}
                            onToggle={() => handleRowToggle(p.id)}
                            checklistMode={checklistMode}
                            isChecked={checkedIds.has(p.id)}
                            onCheck={handleCheck}
                            displayIndex={phaseIndexMap?.[p.id]}
                            isFav={isFav(p.id)}
                            onFavToggle={toggleFav}
                          />
                        );
                      })}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          );
        })}

        {/* ── ⭐ 즐겨찾기 보기 ── */}
        {viewMode === 'fav' && (
          favPrinciples.length === 0 ? (
            /* 비어 있을 때 안내 메시지 */
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex flex-col items-center justify-center py-16 text-center"
            >
              <Star size={40} className="text-slate-700 mb-4" />
              <p className="text-slate-500 text-sm font-medium">즐겨찾기한 원칙이 없어요</p>
              <p className="text-slate-700 text-xs mt-1">원칙 카드 우측의 ⭐를 누르면 여기에 모입니다</p>
            </motion.div>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              className="space-y-2"
            >
              {/* 즐겨찾기 헤더 */}
              <div className="flex items-center gap-2 px-1 mb-3">
                <Star size={14} className="text-amber-400" fill="currentColor" />
                <span className="text-xs font-semibold text-amber-400">즐겨찾기 {favPrinciples.length}개</span>
              </div>
              {favPrinciples.map((p, idx) => {
                const catMeta = CATEGORY_META[p.category];
                return (
                  <motion.div
                    key={p.id}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.2, delay: idx * 0.03 }}
                  >
                    <PrincipleRow
                      principle={p}
                      meta={catMeta}
                      isExpanded={expandedId === p.id}
                      onToggle={() => handleRowToggle(p.id)}
                      checklistMode={checklistMode}
                      isChecked={checkedIds.has(p.id)}
                      onCheck={handleCheck}
                      displayIndex={null}  // 즐겨찾기 모드는 원래 id로
                      isFav={true}
                      onFavToggle={toggleFav}
                    />
                  </motion.div>
                );
              })}
            </motion.div>
          )
        )}

        <p className="text-center text-[11px] text-slate-700 pt-2">
          {viewMode === 'fav'
            ? `⭐ 즐겨찾기 ${favPrinciples.length}가지`
            : levelFilter === 'all'
              ? `총 ${filteredTotal}가지 원칙 · ${viewMode === 'phase' ? '4단계' : `${Object.keys(CATEGORY_META).length}개 영역`}`
              : `${LEVEL_TABS.find(t => t.key === levelFilter)?.label} 필터 · ${filteredTotal}가지 표시 중`}
        </p>
      </div>
    </div>
  );
}
